import { CalendarMonthOutlined, ArrowLeft, ArrowRight } from "@mui/icons-material";
import { DatePicker } from "@mui/lab";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  TextField,
  Button,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { handleKeyDown } from "utils/keyDownPrevent";
import "pages/GridStyling.css";
import { snack } from "components/toast";

type Props = {
  dates: any;
  setDates: (d: { fromDate: Date; toDate: Date }) => void;
  setPage: (n: number) => void;
  setCurrentMonth: (m: any) => void;
  currentMonth: any;
  legend?: string;
  limit: number; // 0 = no limit
};

export default function DateRange({
  setDates: setDatesProp,
  setPage,
  setCurrentMonth,
  legend,
  limit,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /* ✅ Single source of truth for defaults */
  const getDefaultRange = () => ({
    from: moment().startOf("month").toDate(),
    to: moment().toDate(),
  });

  const { from, to } = getDefaultRange();

  /* ✅ Internal DatePicker state */
  const [dates, setDates] = useState<{
    fromDate: Date;
    toDate: Date;
  }>({
    fromDate: from,
    toDate: to,
  });

  /* ✅ Force defaults ONCE on mount */
  useEffect(() => {
    const { from, to } = getDefaultRange();

    setDates({ fromDate: from, toDate: to });
    setDatesProp({ fromDate: from, toDate: to });

    setCurrentMonth(moment(from));
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MIN_DATE = new Date(2020, 0, 1);
  const MAX_DATE = moment().endOf("month").toDate();

  /* ---------------- Month navigation ---------------- */

  const handlePrevMonth = () => {
    const current = moment(dates.fromDate);
    const prev = current.clone().subtract(1, "month");

    if (prev.isBefore(moment("2020-01-01"), "month")) {
      snack.error("Cannot go earlier than Jan 2020");
      return;
    }

    const from = prev.clone().startOf("month").toDate();
    const to = prev.clone().endOf("month").toDate();

    setDates({ fromDate: from, toDate: to });
    setDatesProp({ fromDate: from, toDate: to });
    setCurrentMonth(prev);
    setPage(0);
  };

  const handleNextMonth = () => {
    const current = moment(dates.fromDate);
    const next = current.clone().add(1, "month");
    const today = moment();

    if (next.isAfter(today, "month")) {
      snack.error("Cannot go to future months");
      return;
    }

    const from = next.clone().startOf("month").toDate();
    const to = next.isSame(today, "month")
      ? today.toDate()
      : next.clone().endOf("month").toDate();

    setDates({ fromDate: from, toDate: to });
    setDatesProp({ fromDate: from, toDate: to });
    setCurrentMonth(next);
    setPage(0);
  };

  const disableNext = moment(dates.toDate).isSameOrAfter(moment(), "day");

  /* ---------------- Apply handler (FIXED) ---------------- */

  const handleApply = () => {
    const fromMoment = moment(dates.fromDate);
    const toMoment = moment(dates.toDate);

    /* ❌ Invalid range check */
    if (toMoment.isBefore(fromMoment, "day")) {
      snack.error("Invalid date range");
      return;
    }

    /* ❌ Limit check (only if limit > 0) */
    if (limit > 0) {
      const diff = toMoment.diff(fromMoment, "days");
      if (diff > limit) {
        snack.error(`Date range cannot exceed ${limit} days!`);
        return;
      }
    }

    /* ✅ Valid → update parent */
    setDatesProp({
      fromDate: dates.fromDate,
      toDate: dates.toDate,
    });

    setCurrentMonth(fromMoment);
    setPage(0);
    setAnchorEl(null);
  };

  /* ---------------- UI ---------------- */

  return (
    <>
      <Box className="daterange" width="450px">
        {legend && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "10px" }}
          >
            {legend}
          </Typography>
        )}

        <IconButton size="small" onClick={handlePrevMonth}>
          <ArrowLeft fontSize="small" />
        </IconButton>

        <Typography variant="body2">
          {`${moment(dates.fromDate).format("DD-MM-YYYY")} - ${moment(
            dates.toDate
          ).format("DD-MM-YYYY")}`}
        </Typography>

        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <CalendarMonthOutlined fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={handleNextMonth}
          disabled={disableNext}
        >
          <ArrowRight fontSize="small" />
        </IconButton>
      </Box>

      <Popover
        PaperProps={{ sx: { width: 350, p: 2, mt: 1 } }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <DatePicker
          label="From Date"
          inputFormat="dd-MM-yyyy"
          value={dates.fromDate}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          disableFuture
          onChange={(value) =>
            value && setDates((p) => ({ ...p, fromDate: value }))
          }
          renderInput={(params) => (
            <TextField {...params} fullWidth size="small" onKeyDown={handleKeyDown} />
          )}
        />

        <DatePicker
          label="To Date"
          inputFormat="dd-MM-yyyy"
          value={dates.toDate}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          disableFuture
          onChange={(value) =>
            value && setDates((p) => ({ ...p, toDate: value }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              onKeyDown={handleKeyDown}
            />
          )}
        />

        <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const { from, to } = getDefaultRange();
              setDates({ fromDate: from, toDate: to });
              setDatesProp({ fromDate: from, toDate: to });
              setCurrentMonth(moment(from));
              setAnchorEl(null);
            }}
          >
            Reset
          </Button>

          <Button variant="outlined" size="small" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}
